import { Routine } from 'redux-saga-routines';
import { filter, append, map, prepend, includes } from 'ramda';
import {
  showSurveyBuilderRoutine,
  hideSurveyBuilderRoutine,
  fetchSurveyRoutine,
  changeSurveyNameRoutine,
  changeSurveyDescriptionRoutine,
  fetchQuestionsRoutine,
  setSelectedQuestionsRoutine,
  createQuestionRoutine,
  updateQuestionRoutine,
  fetchAttributesRoutine,
  deleteQuestionRoutine,
  saveSurveyRoutine,
  fetchDefaultQuestionsRoutine,
  checkSurveyNameRoutine
} from '../routines';
import { requiredSurveyQuestion, requiredQuestion } from '../containers/SurveyBuilder/utils';
import { ISurveyData } from '../../../common/models/survey/ISurveyData';
import { ISurveyQuestion } from '../../../common/models/survey/ISurveyQuestion';
import { QuestionType } from '../../../common/enums/QuestionType';
import { IQuestionWithOwner } from '../../../common/models/question/IQuestionWithOwner';
import { IAttribute } from '../../../common/models/attribute/IAttribute';
import { showSurveyBuilderModalRoutine } from '../../../routines/history';
import { QuestionSection } from '../../../common/enums/QuestionSection';

export type SurveyBuilderState = {
  surveyId: string | null;
  questionLoading: boolean;
  surveyLoading: boolean;
  error: string | null;
  surveyData: ISurveyData;
  selectedQuestions: ISurveyQuestion[];
  name: string;
  description: string;
  questions: Record<QuestionSection, IQuestionWithOwner[]>;
  attributes: IAttribute[];
  isNameTaken: boolean;
  isNewNameTaken: boolean;
  isSaving: boolean;
  isSaved: boolean;
  isShown: boolean;
  isChecking: boolean;
};

const initialState = {
  surveyId: null,
  error: null,
  questionLoading: false,
  surveyLoading: true,
  surveyData: {} as ISurveyData,
  selectedQuestions: [],
  name: '',
  description: '',
  questions: {
    [QuestionSection.Questions]: [],
    [QuestionSection.SoftSkills]: []
  },
  attributes: [],
  isNameTaken: false,
  isSaving: false,
  isSaved: false,
  isShown: false,
  isChecking: false,
  isNewNameTaken: false
};

export const surveyBuilder = (state: SurveyBuilderState = initialState, { type, payload }: Routine<any>) => {
  switch (type) {
    case showSurveyBuilderRoutine.TRIGGER:
      return {
        ...state,
        surveyId: payload,
        isShown: true
      };
    case showSurveyBuilderModalRoutine.TRIGGER: {
      return {
        ...state,
        surveyLoading: !!payload,
        questionLoading: true,
        selectedQuestions: []
      };
    }
    case hideSurveyBuilderRoutine.TRIGGER:
      return initialState;
    case fetchSurveyRoutine.REQUEST:
      return {
        ...state,
        surveyLoading: true
      };
    case fetchDefaultQuestionsRoutine.REQUEST:
      return {
        ...state,
        questionLoading: true
      };
    case fetchSurveyRoutine.FAILURE:
      return {
        ...state,
        surveyLoading: false
      };
    case fetchQuestionsRoutine.FAILURE:
    case fetchDefaultQuestionsRoutine.FAILURE:
      return {
        ...state,
        error: payload,
        questionLoading: false
      };
    case fetchSurveyRoutine.SUCCESS:
      return {
        ...state,
        error: null,
        surveyLoading: false,
        surveyData: { ...payload, surveyQuestions: prepend(requiredSurveyQuestion, payload.surveyQuestions) },
        selectedQuestions: prepend(requiredSurveyQuestion, payload.surveyQuestions),
        name: payload.name,
        description: payload.description
      };
    case fetchDefaultQuestionsRoutine.SUCCESS:
      return {
        ...state,
        error: null,
        questionLoading: false,
        selectedQuestions: prepend(requiredSurveyQuestion, payload)
      };
    case changeSurveyNameRoutine.TRIGGER:
      return {
        ...state,
        name: payload
      };
    case changeSurveyDescriptionRoutine.TRIGGER:
      return {
        ...state,
        description: payload
      };
    case fetchQuestionsRoutine.TRIGGER:
      return {
        ...state,
        questionLoading: true
      };
    case fetchQuestionsRoutine.SUCCESS:
      return {
        ...state,
        questionLoading: false,
        questions: {
          ...state.questions,
          [payload.type]: payload.type === QuestionType.Verification
            ? prepend(requiredQuestion, payload.data)
            : payload.data
        },
        error: null
      };
    case setSelectedQuestionsRoutine.TRIGGER:
      const updatedSelectedQuestions = includes(requiredSurveyQuestion, payload)
        ? payload
        : prepend(requiredSurveyQuestion, payload);
      return {
        ...state,
        selectedQuestions: updatedSelectedQuestions
      };
    case createQuestionRoutine.SUCCESS:
      return {
        ...state,
        questions: {
          ...state.questions,
          [QuestionSection.Questions]: append(payload, state.questions[QuestionSection.Questions])
        }
      };
    case updateQuestionRoutine.SUCCESS:
      return {
        ...state,
        questions: {
          ...state.questions,
          [QuestionSection.Questions]: map(
            item => (item.id === payload.id ? { ...item, ...payload } : item),
            state.questions[QuestionSection.Questions]
          )
        }
      };
    case fetchAttributesRoutine.SUCCESS:
      return {
        ...state,
        attributes: payload
      };
    case deleteQuestionRoutine.SUCCESS:
      return {
        ...state,
        questions: {
          ...state.questions,
          [QuestionSection.Questions]: filter(({ id }) => id !== payload, state.questions[QuestionSection.Questions])
        },
        selectedQuestions: filter(({ question }) => question.id !== payload, state.selectedQuestions)
      };
    case saveSurveyRoutine.TRIGGER:
      return {
        ...state,
        isSaving: true
      };
    case saveSurveyRoutine.SUCCESS:
      return {
        ...state,
        isSaved: Boolean(payload.id)
      };
    case saveSurveyRoutine.FULFILL:
      return {
        ...state,
        isSaving: false
      };
    case checkSurveyNameRoutine.TRIGGER:
      return {
        ...state,
        isChecking: true
      };
    case checkSurveyNameRoutine.SUCCESS:
      return {
        ...state,
        isNameTaken: !payload.isSaveAs && payload.isNameTaken,
        isNewNameTaken: payload.isSaveAs && payload.isNameTaken,
        isChecking: false
      };
    case checkSurveyNameRoutine.FAILURE:
      return {
        ...state,
        isChecking: false
      };
    default:
      return state;
  }
};
